import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { useRecoilState } from "recoil";
import { accessTokenState } from "../global-state";
import { v4 as uuidv4 } from "uuid";
import { listen } from "@tauri-apps/api/event";

/**
 * AppInterface Provider for handling cross-platform native calls
 *
 * This provider wraps the native interface to provide a consistent API
 * for Android, iOS, and Desktop (Tauri) platforms:
 * - Android: window.AppInterface (wrapped in async)
 * - iOS: window.webkit.messageHandlers (native async)
 * - Desktop: window.__TAURI_INTERNALS__ (Tauri API)
 *
 * For async methods, the native side should call:
 * - window._resolvePromise(requestId, result) for success
 * - window._rejectPromise(requestId, error) for errors
 */

const AppInterfaceContext = createContext();

export const useAppInterface = () => {
  const context = useContext(AppInterfaceContext);
  if (!context) {
    throw new Error(
      "useAppInterface must be used within an AppInterfaceProvider"
    );
  }
  return context;
};

export const AppInterfaceProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);

  // Use global pending promises map
  const getPendingPromises = useCallback(() => {
    if (typeof window !== "undefined") {
      return window._appInterfacePendingPromises;
    }
    return new Map();
  }, []);

  // Use global websocket messages map
  const getWsMessages = useCallback(() => {
    if (typeof window !== "undefined") {
      return window._wsMessages;
    }
    return new Map();
  }, []);

  // Detect platform and check if native interface is available
  const getPlatform = useCallback(() => {
    if (typeof window === "undefined") return null;

    // Check Tauri first (desktop has priority)
    if (window.__TAURI_INTERNALS__) {
      return "desktop";
    } else if (window.AppInterface) {
      return "android";
    } else if (window.webkit?.messageHandlers) {
      return "ios";
    }

    return null;
  }, []);

  // Check if native interface is available
  const isAvailable = useCallback(() => {
    return getPlatform() !== null;
  }, [getPlatform]);

  // Platform initialization function - extracted for reuse
  const initializePlatform = useCallback(async () => {
    const platformType = getPlatform();

    if (!platformType) {
      return;
    }

    try {
      if (platformType === "android" || platformType === "ios") {
        // Do nothing for mobile app
      } else if (platformType === "desktop") {
        // Desktop initialization
        console.debug("[AppInterface] Initializing desktop platform");
        try {
          const token = await getAccessToken();
          if (token) {
            setAccessToken(token);
          }
        } catch (error) {
          console.error(
            "[AppInterface] Error getting initial access token:",
            error
          );
        }

        // Set up event listeners for auth updates
        const handleTokenUpdate = (newToken) => {
          if (newToken) {
            setAccessToken(newToken);
          }
        };

        const handleAuthError = (errorData) => {
          console.debug("[AppInterface] Desktop auth error:", errorData);
          if (errorData?.shouldRedirect && typeof window !== "undefined") {
            window.location.href = "/user-login";
          }
        };

        // Add event listeners
        addEventListener("auth:tokens-updated", handleTokenUpdate);
        addEventListener("auth:error", handleAuthError);

        // Store cleanup function for later use
        return () => {
          removeEventListener("auth:tokens-updated", handleTokenUpdate);
          removeEventListener("auth:error", handleAuthError);
        };
      }

      console.debug("[AppInterface] Platform initialization complete");
    } catch (error) {
      console.error(
        "[AppInterface] Error during platform initialization:",
        error
      );
    }
  }, []);

  // Internal platform initialization - runs automatically on mount
  useEffect(() => {
    initializePlatform();
  }, [initializePlatform]);

  // Get the appropriate native interface
  const getNativeInterface = useCallback(() => {
    const platform = getPlatform();

    if (platform === "android") {
      return window.AppInterface;
    } else if (platform === "desktop") {
      return window.__TAURI_INTERNALS__;
    } else if (platform === "ios") {
      return window.webkit.messageHandlers;
    }

    return null;
  }, [getPlatform]);

  // Generic async call helper for websocket messages on Android
  const callAndroidWsAsync = useCallback(
    async (methodName, interfaceCall, timeout = 30000) => {
      return new Promise((resolve, reject) => {
        try {
          const sig = uuidv4();

          const promiseData = { resolve, reject };
          getWsMessages().set(sig, promiseData);

          const timeoutId = setTimeout(() => {
            const wsMap = getWsMessages();
            if (wsMap.has(sig)) {
              wsMap.delete(sig);
              reject(
                new Error(
                  `Android websocket request timeout after ${timeout}ms`
                )
              );
            }
          }, timeout);

          const originalResolve = promiseData.resolve;
          const originalReject = promiseData.reject;

          promiseData.resolve = (result) => {
            clearTimeout(timeoutId);
            originalResolve(result);
          };

          promiseData.reject = (error) => {
            clearTimeout(timeoutId);
            originalReject(error);
          };

          // Call the provided interface method with requestId
          interfaceCall(sig);
        } catch (error) {
          reject(
            new Error(
              `Failed to call Android websocket ${methodName}: ${error.message}`
            )
          );
        }
      });
    },
    [getWsMessages]
  );

  // Generic async call helper for websocket messages on iOS
  // TODO
  const callIosWsMethodAsync = useCallback(
    async (handlerName, message, timeout = 30000) => {
      const messageHandlers = window.webkit.messageHandlers;
      const handler = messageHandlers[handlerName];

      return new Promise((resolve, reject) => {
        try {
          // Generate request ID for tracking the async response
          const sig = uuidv4();

          // Store the promise callbacks with the requestId
          const promiseData = { resolve, reject };
          getWsMessages().set(sig, promiseData);

          // Set up timeout
          const timeoutId = setTimeout(() => {
            const wsMap = getWsMessages();
            if (wsMap.has(sig)) {
              wsMap.delete(sig);
              reject(
                new Error(`iOS websocket request timeout after ${timeout}ms`)
              );
            }
          }, timeout);

          // Clear timeout when promise resolves/rejects
          const originalResolve = promiseData.resolve;
          const originalReject = promiseData.reject;

          promiseData.resolve = (result) => {
            console.debug(`iOS websocket method ${handlerName} resolved`);
            clearTimeout(timeoutId);
            originalResolve(result);
          };

          promiseData.reject = (error) => {
            console.debug(
              `iOS websocket method ${handlerName} rejected`,
              error.message
            );
            clearTimeout(timeoutId);
            originalReject(error);
          };

          // Call iOS handler with requestId included in the message
          const messageWithId = { ...message, sig };
          handler.postMessage(messageWithId);
        } catch (error) {
          console.debug(
            `iOS websocket method ${handlerName} failed`,
            error.message
          );
          reject(
            new Error(
              `Failed to call iOS webkit websocket handler '${handlerName}': ${error.message}`
            )
          );
        }
      });
    },
    [getWsMessages]
  );

  // Generic async call helper for iOS
  const callIosMethodAsync = useCallback(
    async (handlerName, message, timeout = 30000) => {
      const messageHandlers = window.webkit.messageHandlers;
      const handler = messageHandlers[handlerName];

      if (!handler || typeof handler.postMessage !== "function") {
        throw new Error(`iOS webkit handler '${handlerName}' not found`);
      }

      return new Promise((resolve, reject) => {
        try {
          // Generate request ID for tracking the async response
          const requestId = `${handlerName}_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;

          // Store the promise callbacks with the requestId
          const promiseData = { resolve, reject };
          getPendingPromises().set(requestId, promiseData);

          // Set up timeout
          const timeoutId = setTimeout(() => {
            const pendingMap = getPendingPromises();
            if (pendingMap.has(requestId)) {
              pendingMap.delete(requestId);
              reject(new Error(`iOS request timeout after ${timeout}ms`));
            }
          }, timeout);

          // Clear timeout when promise resolves/rejects
          const originalResolve = promiseData.resolve;
          const originalReject = promiseData.reject;

          promiseData.resolve = (result) => {
            console.debug(`iOS method ${handlerName} resolved`);
            clearTimeout(timeoutId);
            originalResolve(result);
          };

          promiseData.reject = (error) => {
            console.debug(`iOS method ${handlerName} rejected`, error.message);
            clearTimeout(timeoutId);
            originalReject(error);
          };

          // Call iOS handler with requestId included in the message
          const messageWithId = { ...message, requestId };
          handler.postMessage(messageWithId);
        } catch (error) {
          console.debug(`iOS method ${handlerName} failed`, error.message);
          reject(
            new Error(
              `Failed to call iOS webkit handler '${handlerName}': ${error.message}`
            )
          );
        }
      });
    },
    []
  );

  // Generic async call helper for Android
  const callAndroidAsync = useCallback(
    async (methodName, interfaceCall, timeout = 30000) => {
      return new Promise((resolve, reject) => {
        try {
          const requestId = `${methodName}_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`;

          const promiseData = { resolve, reject };
          getPendingPromises().set(requestId, promiseData);

          const timeoutId = setTimeout(() => {
            const pendingMap = getPendingPromises();
            if (pendingMap.has(requestId)) {
              pendingMap.delete(requestId);
              reject(new Error(`Android request timeout after ${timeout}ms`));
            }
          }, timeout);

          const originalResolve = promiseData.resolve;
          const originalReject = promiseData.reject;

          promiseData.resolve = (result) => {
            clearTimeout(timeoutId);
            originalResolve(result);
          };

          promiseData.reject = (error) => {
            clearTimeout(timeoutId);
            originalReject(error);
          };

          // Call the provided interface method with requestId
          interfaceCall(requestId);
        } catch (error) {
          reject(
            new Error(`Failed to call Android ${methodName}: ${error.message}`)
          );
        }
      });
    },
    [getPendingPromises]
  );

  // Generic async call helper for Desktop (Tauri)
  const callDesktopAsync = useCallback(async (method, args = {}) => {
    try {
      if (!window.__TAURI_INTERNALS__) {
        throw new Error("Tauri is not available");
      }
      return await window.__TAURI_INTERNALS__.invoke(method, args);
    } catch (error) {
      throw new Error(
        `Failed to call desktop method '${method}': ${error.message}`
      );
    }
  }, []);
  const callDesktopSend = useCallback((method, args = {}) => {
    try {
      if (!window.__TAURI_INTERNALS__) {
        throw new Error("Tauri is not available");
      }
      window.__TAURI_INTERNALS__.invoke(method, args).catch((error) => {
        console.error(`[AppInterface] Tauri send failed: ${method}`, error);
      });
    } catch (error) {
      throw new Error(
        `Failed to send desktop method '${method}': ${error.message}`
      );
    }
  }, []);

  // Generic event listener helper for Desktop (Tauri)
  const addDesktopListener = useCallback((event, callback) => {
    try {
      listen(event, (tauriEvent) => {
        callback(tauriEvent.payload);
      })
        .then((unlisten) => {
          // Store unlisten function for cleanup
          if (!window._tauriUnlisteners) {
            window._tauriUnlisteners = new Map();
          }
          const key = `${event}_${callback.toString()}`;
          window._tauriUnlisteners.set(key, unlisten);
        })
        .catch((error) => {
          console.error(
            `Failed to add desktop listener for '${event}':`,
            error
          );
        });
    } catch (error) {
      console.error(`Failed to add desktop listener for '${event}':`, error);
    }
  }, []);

  const removeDesktopListener = useCallback((event, callback) => {
    try {
      if (window._tauriUnlisteners) {
        const key = `${event}_${callback.toString()}`;
        const unlisten = window._tauriUnlisteners.get(key);
        if (unlisten) {
          unlisten();
          window._tauriUnlisteners.delete(key);
        }
      }
    } catch (error) {
      console.error(`Failed to remove desktop listener for '${event}':`, error);
    }
  }, []);

  // Specific async method implementations

  // Auth methods
  const getAccessToken = useCallback(async () => {
    const platform = getPlatform();

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("getAccessToken", (requestId) => {
        window.AppInterface.getAccessToken(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("getAccessToken", {});
    } else if (platform === "desktop") {
      return await callDesktopAsync("auth_get_access_token");
    }
    throw new Error("Unsupported platform");
  }, [
    isAvailable,
    getPlatform,
    callIosMethodAsync,
    callAndroidAsync,
    callDesktopAsync,
  ]);

  const logout = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] logout called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("logout", (requestId) => {
        window.AppInterface.logout(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("logout", {});
    } else if (platform === "desktop") {
      return callDesktopSend("auth_logout");
    }
    throw new Error("Unsupported platform");
  }, [
    isAvailable,
    getPlatform,
    callIosMethodAsync,
    callAndroidAsync,
    callDesktopSend,
  ]);

  const saveUserInfo = useCallback(
    async (userInfoJson) => {
      const platform = getPlatform();
      if (platform === "android") {
        return await callAndroidAsync("saveUserInfo", (requestId) => {
          window.AppInterface.saveUserInfo(
            requestId,
            JSON.stringify(userInfoJson)
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("saveUserInfo", {
          userInfo: JSON.stringify(userInfoJson),
        });
      } else if (platform === "desktop") {
        return await callDesktopAsync("user_info_save", userInfoJson);
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const saveScopes = useCallback(
    async (scopesJson) => {
      const platform = getPlatform();
      if (platform === "android") {
        return await callAndroidAsync("saveScopes", (requestId) => {
          window.AppInterface.saveScopes(requestId, JSON.stringify(scopesJson));
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("saveScopes", {
          scopes: JSON.stringify(scopesJson),
        });
      } else if (platform === "desktop") {
        return await callDesktopAsync("scopes_save", scopesJson);
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const openAuthUrl = useCallback(
    async (url) => {
      const platform = getPlatform();

      if (platform === "android") {
        return await callAndroidAsync("openAuthUrl", (requestId) => {
          window.AppInterface.openAuthUrl(requestId, url);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("openAuthUrl", { url });
      } else if (platform === "desktop") {
        return callDesktopSend("open_external_url", { url });
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopSend,
    ]
  );

  // Device/System methods
  const getDeviceInfo = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] getDeviceInfo called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("getDeviceInfo", (requestId) => {
        window.AppInterface.getDeviceInfo(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("getDeviceInfo", {});
    } else if (platform === "desktop") {
      return await callDesktopAsync("get_device_info");
    }
    throw new Error("Unsupported platform");
  }, [
    isAvailable,
    getPlatform,
    callIosMethodAsync,
    callAndroidAsync,
    callDesktopAsync,
  ]);

  // Hub methods
  const startHub = useCallback(
    async (scopeId, hubId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] startHub called", {
        platform,
        scopeId,
        hubId,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("startHub", (requestId) => {
          window.AppInterface.startHub(requestId, scopeId, hubId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("startHub", { scopeId, hubId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_start", { scopeId, hubId });
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const stopHub = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] stopHub called", { platform, scopeId });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("stopHub", (requestId) => {
          window.AppInterface.stopHub(requestId, scopeId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("stopHub", { scopeId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_stop", { scopeId });
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const getHubCandidates = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] getHubCandidates called", {
        platform,
        scopeId,
      });

      if (!isAvailable()) {
        return { candidates: [] };
      }

      if (platform === "android") {
        return await callAndroidAsync("getHubCandidates", (requestId) => {
          window.AppInterface.getHubCandidates(requestId, scopeId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("getHubCandidates", { scopeId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_get_candidates", scopeId);
      }
      return { candidates: [] };
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const initHub = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] initHub called", { platform, scopeId });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("initHub", (requestId) => {
          window.AppInterface.initHub(requestId, scopeId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("initHub", { scopeId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_init", scopeId);
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const getOnDeviceHubInstance = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] getOnDeviceHubInstance called", {
        platform,
        scopeId,
      });

      if (!isAvailable()) {
        return { error: "Native interface not available" };
      }

      if (platform === "android") {
        return await callAndroidAsync("getOnDeviceHubInstance", (requestId) => {
          window.AppInterface.getOnDeviceHubInstance(requestId, scopeId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("getOnDeviceHubInstance", { scopeId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_get_on_device_instance", {
          scopeId,
        });
      }
      return { error: "Unsupported platform" };
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  const diagnoseOnDeviceHub = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] diagnoseOnDeviceHub called", {
        platform,
        scopeId,
      });

      if (!isAvailable()) {
        return { error: "Native interface not available" };
      }

      if (platform === "android") {
        return await callAndroidAsync("diagnoseOnDeviceHub", (requestId) => {
          window.AppInterface.diagnoseOnDeviceHub(requestId, scopeId);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("diagnoseOnDeviceHub", { scopeId });
      } else if (platform === "desktop") {
        return await callDesktopAsync("hub_diagnose_on_device", { scopeId });
      }
      return { error: "Unsupported platform" };
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  // API methods
  const apiCall = useCallback(
    async (endpoint, method, headers, body, baseUrl) => {
      const platform = getPlatform();

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("apiCall", (requestId) => {
          window.AppInterface.apiCall(
            requestId,
            endpoint,
            method,
            headers,
            body,
            baseUrl
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("apiCall", {
          endpoint,
          method,
          headers,
          body,
          baseUrl,
        });
      } else if (platform === "desktop") {
        return await callDesktopAsync("api_call", {
          endpoint,
          method,
          headers,
          body,
          baseUrl,
        });
      }
      throw new Error("Unsupported platform");
    },
    [
      isAvailable,
      getPlatform,
      callIosMethodAsync,
      callAndroidAsync,
      callDesktopAsync,
    ]
  );

  // Desktop permission methods
  const getDesktopInfo = useCallback(
    async (scopeId) => {
      const platform = getPlatform();
      console.debug("[AppInterface] getDesktopInfo called", {
        platform,
        scopeId,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "desktop") {
        return await callDesktopAsync("desktop_info", { scopeId });
      }
      throw new Error("Desktop method not supported on this platform");
    },
    [isAvailable, getPlatform, callDesktopAsync]
  );

  const getDesktopCategories = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] getDesktopCategories called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "desktop") {
      return await callDesktopAsync("desktop_all_category");
    }
    throw new Error("Desktop method not supported on this platform");
  }, [isAvailable, getPlatform, callDesktopAsync]);

  const enableDisableDesktop = useCallback(
    async (scopeId, enable) => {
      const platform = getPlatform();
      console.debug("[AppInterface] enableDisableDesktop called", {
        platform,
        scopeId,
        enable,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "desktop") {
        return await callDesktopAsync("desktop_enable_disable", {
          scopeId,
          enable,
        });
      }
      throw new Error("Desktop method not supported on this platform");
    },
    [isAvailable, getPlatform, callDesktopAsync]
  );

  const setDesktopDataCategory = useCallback(
    async (scopeId, categoryId, enable) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setDesktopDataCategory called", {
        platform,
        scopeId,
        categoryId,
        enable,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "desktop") {
        return await callDesktopAsync("desktop_enable_disable_category", {
          scopeId,
          categoryId,
          enable,
        });
      }
      throw new Error("Desktop method not supported on this platform");
    },
    [isAvailable, getPlatform, callDesktopAsync]
  );

  const setDesktopCapability = useCallback(
    async (scopeId, capabilityId, enabled) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setDesktopCapability called", {
        platform,
        scopeId,
        capabilityId,
        enabled,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "desktop") {
        return await callDesktopAsync("desktop_enable_disable_capability", {
          scopeId,
          capabilityId,
          enabled,
        });
      }
      throw new Error("Desktop method not supported on this platform");
    },
    [isAvailable, getPlatform, callDesktopAsync]
  );

  // Phone permission methods (for mobile platforms)
  const getPhoneInfo = useCallback(async () => {
    const platform = getPlatform();
    if (platform === "android") {
      return await callAndroidAsync("getPhoneInfo", (requestId) => {
        window.AppInterface.getPhoneInfo(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("getPhoneInfo", {});
    }
    throw new Error("Phone method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const getPhoneCategories = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] getPhoneCategories called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("getPhoneCategories", (requestId) => {
        window.AppInterface.getPhoneCategories(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("getPhoneCategories", {});
    }
    throw new Error("Phone method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const enableDisablePhone = useCallback(
    async (enable) => {
      const platform = getPlatform();
      if (platform === "android") {
        return await callAndroidAsync("enableDisablePhone", (requestId) => {
          window.AppInterface.enableDisablePhone(requestId, enable);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("enableDisablePhone", {
          enable,
        });
      }
      throw new Error("Phone method not supported on this platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setPhoneDataCategory = useCallback(
    async (categoryId, enable) => {
      const platform = getPlatform();

      if (platform === "android") {
        return await callAndroidAsync("setPhoneDataCategory", (requestId) => {
          window.AppInterface.setPhoneDataCategory(
            requestId,
            categoryId,
            enable
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setPhoneDataCategory", {
          categoryId,
          enable,
        });
      }
      throw new Error("Phone method not supported on this platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setPhoneDataCapability = useCallback(
    async (capabilityId, enabled) => {
      const platform = getPlatform();

      if (platform === "android") {
        return await callAndroidAsync("setPhoneDataCapability", (requestId) => {
          window.AppInterface.setPhoneDataCapability(
            requestId,
            capabilityId,
            enabled
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setPhoneDataCapability", {
          capabilityId,
          enabled,
        });
      }
      throw new Error("Phone method not supported on this platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  // Phone notification methods
  const setNotificationSetting = useCallback(
    async (settings) => {
      const platform = getPlatform();
      if (platform === "android") {
        return await callAndroidAsync("setNotificationSetting", (requestId) => {
          window.AppInterface.setNotificationSetting(
            requestId,
            JSON.stringify(settings)
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setNotificationSetting", {
          settings: JSON.stringify(settings),
        });
      }
      throw new Error(
        "Phone notification method not supported on this platform"
      );
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setPhoneDataReportMode = useCallback(
    async (mode) => {
      const platform = getPlatform();
      if (platform === "android") {
        return await callAndroidAsync("setPhoneDataReportMode", (requestId) => {
          window.AppInterface.setPhoneDataReportMode(requestId, mode);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setPhoneDataReportMode", {
          mode,
        });
      }
      throw new Error("Phone method not supported on this platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  // Websocket methods
  const sendWebsocketAndWait = useCallback(
    async (scopeId, target, payload) => {
      const platform = getPlatform();
      const type = "arq";
      if (platform === "android") {
        return await callAndroidWsAsync("sendWebsocketAndWait", (sig) => {
          window.AppInterface.sendWebsocketAndWait(
            scopeId,
            target,
            sig,
            payload,
            type
          );
        });
      } else if (platform === "ios") {
        return await callIosWsMethodAsync("sendWebsocketAndWait", {
          scopeId,
          target,
          payload,
          type: "arq",
        });
      } else if (platform === "desktop") {
        return await callDesktopAsync("ws_send_and_wait", {
          scopeId,
          target,
          payload,
          type,
        });
      }
    },
    [getPlatform, callAndroidWsAsync, callIosWsMethodAsync, callDesktopAsync]
  );

  const sendWebsocketAndForget = useCallback(
    (scopeId, target, payload) => {
      const platform = getPlatform();
      const type = "ad";
      if (platform === "android") {
        return window.AppInterface.sendWebsocketAndForget(
          scopeId,
          target,
          uuidv4(),
          JSON.stringify(payload),
          type
        );
      } else if (platform === "ios") {
        const messageHandlers = window.webkit.messageHandlers;
        const handler = messageHandlers.sendWebsocketAndForget;
        return handler.postMessage({
          scopeId,
          target,
          sig: uuidv4(),
          payload,
          type: "ad",
        });
      } else if (platform === "desktop") {
        return callDesktopSend("ws_send_and_forget", {
          scopeId,
          target,
          payload,
          type,
        });
      }
    },
    [isAvailable, getPlatform, callDesktopSend]
  );

  const getWebsocketStatus = useCallback(async () => {
    const platform = getPlatform();
    if (platform === "desktop") {
      return await callDesktopAsync("ws_status");
    }
  }, [isAvailable, getPlatform, callDesktopAsync]);

  // Synchronous version for mobile platforms to check WS status
  const getWsStatus = useCallback(() => {
    const platform = getPlatform();
    console.debug("[AppInterface] getWsStatus called");

    if (platform === "android") {
      // Android sync method to get websocket status
      try {
        const result = window.AppInterface.getWsStatus();
        return typeof result === "string" ? JSON.parse(result) : result;
      } catch (error) {
        console.warn(
          "[AppInterface] Failed to get WS status from Android:",
          error
        );
        return { ready: false };
      }
    } else if (platform === "ios") {
      // iOS sync method to get websocket status
      try {
        const messageHandlers = window.webkit.messageHandlers;
        const handler = messageHandlers.getWsStatus;
        if (handler) {
          // For iOS, we'll need to use a sync approach or store the status in a global variable
          // Since webkit messageHandlers are async, we'll use a fallback approach
          return window._wsStatusCache || { ready: false };
        }
        return { ready: false };
      } catch (error) {
        console.warn("[AppInterface] Failed to get WS status from iOS:", error);
        return { ready: false };
      }
    }

    return { ready: false };
  }, [getPlatform]);

  const refreshWebsocketConnection = useCallback(() => {
    const platform = getPlatform();
    if (platform === "android") {
      window.AppInterface.refreshWebsocketConnection();
    } else if (platform === "ios") {
      const messageHandlers = window.webkit.messageHandlers;
      const handler = messageHandlers.refreshWebsocketConnection;
      if (handler) {
        handler.postMessage({});
      }
    } else if (platform === "desktop") {
      callDesktopSend("ws_reconnect");
    }
  }, [isAvailable, getPlatform, callDesktopSend]);

  // Software update methods (desktop only)
  const checkForUpdates = useCallback(() => {
    const platform = getPlatform();
    console.debug("[AppInterface] checkForUpdates called", { platform });
    if (platform === "desktop") {
      return callDesktopSend("check_for_updates");
    }
  }, [isAvailable, getPlatform, callDesktopSend]);

  const downloadUpdate = useCallback(() => {
    const platform = getPlatform();
    console.debug("[AppInterface] downloadUpdate called", { platform });
    if (platform === "desktop") {
      return callDesktopSend("download_update");
    }
  }, [isAvailable, getPlatform, callDesktopSend]);

  const installUpdate = useCallback(() => {
    const platform = getPlatform();
    console.debug("[AppInterface] installUpdate called", { platform });
    if (platform === "desktop") {
      return callDesktopSend("install_update");
    }
  }, [isAvailable, getPlatform, callDesktopSend]);

  // Notification methods (desktop only)
  const sendNotification = useCallback(
    (notification) => {
      const platform = getPlatform();
      console.debug("[AppInterface] sendNotification called");
      if (platform === "desktop") {
        return callDesktopSend("send_notification", notification);
      }
    },
    [isAvailable, getPlatform, callDesktopSend]
  );

  const checkNotificationPermission = useCallback(async () => {
    const platform = getPlatform();
    if (platform === "desktop") {
      try {
        return await callDesktopAsync("check_notification_permission");
      } catch (error) {
        console.warn(
          "[AppInterface] Failed to check notification permission:",
          error
        );
        return false;
      }
    }
    return false;
  }, [isAvailable, getPlatform, callDesktopAsync]);

  // OAuth methods (desktop only)
  const handleOAuth = useCallback(
    (oauthData) => {
      const platform = getPlatform();
      console.debug("[AppInterface] handleOAuth called", {
        platform,
        oauthData,
      });
      if (platform === "desktop") {
        return callDesktopSend("oauth", oauthData);
      }
    },
    [isAvailable, getPlatform, callDesktopSend]
  );

  // Developer auth methods (desktop only)
  const devVerifyAuth = useCallback(
    async (authData) => {
      const platform = getPlatform();
      console.debug("[AppInterface] devVerifyAuth called");
      if (platform === "desktop") {
        return await callDesktopAsync("auth_dev_verify", authData);
      }
    },
    [isAvailable, getPlatform, callDesktopAsync]
  );

  // Event listener management methods
  const addEventListener = useCallback(
    (event, callback) => {
      const platform = getPlatform();
      if (platform === "desktop") {
        return addDesktopListener(event, callback);
      }
    },
    [isAvailable, getPlatform, addDesktopListener]
  );

  const removeEventListener = useCallback(
    (event, callback) => {
      const platform = getPlatform();
      console.debug("[AppInterface] removeEventListener called", {
        platform,
        event,
      });
      if (platform === "desktop") {
        return removeDesktopListener(event, callback);
      }
    },
    [isAvailable, getPlatform, removeDesktopListener]
  );

  // Assistant input methods
  const showAssistantInput = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] showAssistantInput called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("showAssistantInput", (requestId) => {
        window.AppInterface.showAssistantInput(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("showAssistantInput", {});
    }
    throw new Error("Assistant method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const hideAssistantInput = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] hideAssistantInput called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("hideAssistantInput", (requestId) => {
        window.AppInterface.hideAssistantInput(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("hideAssistantInput", {});
    }
    throw new Error("Assistant method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const setAssistantInputText = useCallback(
    async (text, prompt) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantInputText called", {
        platform,
        text,
        prompt,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("setAssistantInputText", (requestId) => {
          window.AppInterface.setAssistantInputText(requestId, text, prompt);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantInputText", {
          text,
          prompt,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const getAssistantInputText = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] getAssistantInputText called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("getAssistantInputText", (requestId) => {
        window.AppInterface.getAssistantInputText(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("getAssistantInputText", {});
    }
    throw new Error("Assistant method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const setAssistantInputPlaceholder = useCallback(
    async (placeholder) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantInputPlaceholder called", {
        platform,
        placeholder,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync(
          "setAssistantInputPlaceholder",
          (requestId) => {
            window.AppInterface.setAssistantInputPlaceholder(
              requestId,
              placeholder
            );
          }
        );
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantInputPlaceholder", {
          placeholder,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setAssistantPrompts = useCallback(
    async (promptsJson) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantPrompts called", {
        platform,
        promptsJson,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("setAssistantPrompts", (requestId) => {
          window.AppInterface.setAssistantPrompts(requestId, promptsJson);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantPrompts", {
          prompts: promptsJson,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const focusAssistantInput = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] focusAssistantInput called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("focusAssistantInput", (requestId) => {
        window.AppInterface.focusAssistantInput(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("focusAssistantInput", {});
    }
    throw new Error("Assistant method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const blurAssistantInput = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] blurAssistantInput called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("blurAssistantInput", (requestId) => {
        window.AppInterface.blurAssistantInput(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("blurAssistantInput", {});
    }
    throw new Error("Assistant method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  const setAssistantEntityTypes = useCallback(
    async (entityTypesJson) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantEntityTypes called", {
        platform,
        entityTypesJson,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync(
          "setAssistantEntityTypes",
          (requestId) => {
            window.AppInterface.setAssistantEntityTypes(
              requestId,
              entityTypesJson
            );
          }
        );
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantEntityTypes", {
          entityTypes: entityTypesJson,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setAssistantEntities = useCallback(
    async (entityType, entitiesJson) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantEntities called", {
        platform,
        entityType,
        entitiesJson,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("setAssistantEntities", (requestId) => {
          window.AppInterface.setAssistantEntities(
            requestId,
            entityType,
            entitiesJson
          );
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantEntities", {
          entityType,
          entities: entitiesJson,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setAssistantQuickWords = useCallback(
    async (quickWordsJson) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantQuickWords called", {
        platform,
        quickWordsJson,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync("setAssistantQuickWords", (requestId) => {
          window.AppInterface.setAssistantQuickWords(requestId, quickWordsJson);
        });
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantQuickWords", {
          quickWords: quickWordsJson,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  const setAssistantProcessedQuickword = useCallback(
    async (processedText, error) => {
      const platform = getPlatform();
      console.debug("[AppInterface] setAssistantProcessedQuickword called", {
        platform,
        processedText,
        error,
      });

      if (!isAvailable()) {
        throw new Error("Native interface not available");
      }

      if (platform === "android") {
        return await callAndroidAsync(
          "setAssistantProcessedQuickword",
          (requestId) => {
            window.AppInterface.setAssistantProcessedQuickword(
              requestId,
              processedText,
              error
            );
          }
        );
      } else if (platform === "ios") {
        return await callIosMethodAsync("setAssistantProcessedQuickword", {
          processedText,
          error,
        });
      }
      throw new Error("Unsupported platform");
    },
    [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]
  );

  // Google Home integration methods
  const connectGoogleHome = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] connectGoogleHome called", { platform });

    if (!isAvailable()) {
      throw new Error("Native interface not available");
    }

    if (platform === "android") {
      return await callAndroidAsync("connectGoogleHome", (requestId) => {
        window.AppInterface.connectGoogleHome(requestId);
      });
    } else if (platform === "ios") {
      return await callIosMethodAsync("connectGoogleHome", {});
    }
    throw new Error("Google Home method not supported on this platform");
  }, [isAvailable, getPlatform, callIosMethodAsync, callAndroidAsync]);

  // NFC methods
  const enterNfcWrite = useCallback(
    async (str) => {
      const platform = getPlatform();
      console.debug("[AppInterface] enterNfcWrite called", { platform, str });

      if (platform === "android") {
        try {
          const result = await window.AppInterface.enterNfcWrite(str);
          // Parse result if it's a JSON string, otherwise assume it's already an object
          if (typeof result === "string") {
            return JSON.parse(result);
          }
          return result;
        } catch (error) {
          return { success: false, error: error.message };
        }
      } else if (platform === "ios") {
        return await callIosMethodAsync("enterNfcWrite", { data: str });
      }

      return {
        success: false,
        error: "NFC method not supported on this platform",
      };
    },
    [isAvailable, getPlatform, callIosMethodAsync]
  );

  const cancelNfcWrite = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] cancelNfcWrite called", { platform });

    if (platform === "android") {
      try {
        const result = await window.AppInterface.cancelNfcWrite();
        // Parse result if it's a JSON string, otherwise assume it's already an object
        if (typeof result === "string") {
          return JSON.parse(result);
        }
        return result;
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else if (platform === "ios") {
      return await callIosMethodAsync("cancelNfcWrite", {});
    }

    return {
      success: false,
      error: "NFC method not supported on this platform",
    };
  }, [isAvailable, getPlatform, callIosMethodAsync]);

  const enterNfcCheck = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] enterNfcCheck called", { platform });

    if (platform === "android") {
      try {
        const result = await window.AppInterface.enterNfcCheck();
        // Parse result if it's a JSON string, otherwise assume it's already an object
        if (typeof result === "string") {
          return JSON.parse(result);
        }
        return result;
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else if (platform === "ios") {
      return await callIosMethodAsync("enterNfcCheck", {});
    }

    return {
      success: false,
      error: "NFC method not supported on this platform",
    };
  }, [isAvailable, getPlatform, callIosMethodAsync]);

  const cancelNfcCheck = useCallback(async () => {
    const platform = getPlatform();
    console.debug("[AppInterface] cancelNfcCheck called", { platform });

    if (platform === "android") {
      try {
        const result = await window.AppInterface.cancelNfcCheck();
        // Parse result if it's a JSON string, otherwise assume it's already an object
        if (typeof result === "string") {
          return JSON.parse(result);
        }
        return result;
      } catch (error) {
        return { success: false, error: error.message };
      }
    } else if (platform === "ios") {
      return await callIosMethodAsync("cancelNfcCheck", {});
    }

    return {
      success: false,
      error: "NFC method not supported on this platform",
    };
  }, [isAvailable, getPlatform, callIosMethodAsync]);

  // Direct access to native interface (for backwards compatibility)
  const native = useCallback(() => {
    if (!isAvailable()) {
      return null;
    }
    return getNativeInterface();
  }, [isAvailable, getNativeInterface]);

  // Get current platform
  const platform = useCallback(() => {
    return getPlatform();
  }, [getPlatform]);

  // Clean up pending promises and websocket messages
  const cleanup = useCallback(() => {
    const pendingMap = getPendingPromises();
    pendingMap.forEach((promiseData) => {
      promiseData.reject(new Error("AppInterface cleanup"));
    });
    pendingMap.clear();

    const wsMap = getWsMessages();
    wsMap.forEach((promiseData) => {
      promiseData.reject(new Error("AppInterface websocket cleanup"));
    });
    wsMap.clear();
  }, [getPendingPromises, getWsMessages]);

  // Get pending promises count (for debugging)
  const getPendingCount = useCallback(() => {
    return getPendingPromises().size;
  }, [getPendingPromises]);

  // Get pending websocket messages count (for debugging)
  const getWsPendingCount = useCallback(() => {
    return getWsMessages().size;
  }, [getWsMessages]);

  const contextValue = {
    isAvailable,
    platform,
    native,
    cleanup,
    getPendingCount,
    getWsPendingCount,
    // Auth methods
    getAccessToken,
    logout,
    saveUserInfo,
    saveScopes,
    openAuthUrl,
    devVerifyAuth,
    // Device/System methods
    getDeviceInfo,
    // Hub methods
    startHub,
    stopHub,
    getHubCandidates,
    initHub,
    getOnDeviceHubInstance,
    diagnoseOnDeviceHub,
    // API methods
    apiCall,
    // Desktop permission methods (desktop only)
    getDesktopInfo,
    getDesktopCategories,
    enableDisableDesktop,
    setDesktopDataCategory,
    setDesktopCapability,
    // Phone permission methods (mobile only)
    getPhoneInfo,
    getPhoneCategories,
    enableDisablePhone,
    setPhoneDataCategory,
    setPhoneDataCapability,
    setNotificationSetting,
    setPhoneDataReportMode,
    // Websocket methods (desktop only)
    sendWebsocketAndWait,
    sendWebsocketAndForget,
    getWebsocketStatus,
    getWsStatus,
    refreshWebsocketConnection,
    // Software update methods (desktop only)
    checkForUpdates,
    downloadUpdate,
    installUpdate,
    // Notification methods (desktop only)
    sendNotification,
    checkNotificationPermission,
    // OAuth methods (desktop only)
    handleOAuth,
    // Event listener methods (desktop only)
    addEventListener,
    removeEventListener,
    // Assistant input methods
    showAssistantInput,
    hideAssistantInput,
    setAssistantInputText,
    getAssistantInputText,
    setAssistantInputPlaceholder,
    setAssistantPrompts,
    focusAssistantInput,
    blurAssistantInput,
    setAssistantEntityTypes,
    setAssistantEntities,
    setAssistantQuickWords,
    setAssistantProcessedQuickword,
    // Google Home integration methods
    connectGoogleHome,
    // NFC methods
    enterNfcWrite,
    cancelNfcWrite,
    enterNfcCheck,
    cancelNfcCheck,
  };

  return (
    <AppInterfaceContext.Provider value={contextValue}>
      {children}
    </AppInterfaceContext.Provider>
  );
};
